/**
 * Tabela Orçamentária Page
 *
 * Budget table view page with expandable phases and line items
 * Provides detailed tabular view of budget execution
 *
 * Features:
 * - Project selector
 * - Search and filter functionality
 * - Expandable phase sections
 * - CRUD operations on line items
 * - Navigation back to card view
 */

import { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { AppContainer } from "@/components/layout/app-container";
import { Button } from "@/components/ui/button";
import { ProjectSelector } from "@/components/orcamentos/project-selector";
import { BudgetTable } from "@/components/orcamentos/budget-table";
import { DeletePhaseDialog } from "@/components/orcamentos/delete-phase-dialog";
import { BudgetLineItemDialog } from "@/components/orcamentos/budget-line-item-dialog";
import { BudgetSubitemDialog } from "@/components/orcamentos/budget-subitem-dialog";
import { PaymentFormDialog } from "@/components/orcamentos/payment-form-dialog";
import { toast } from "@/hooks/use-toast";
import {
  useCreateBudgetLineItem,
  useUpdateBudgetLineItem,
  useUploadNFBudget,
  useDeleteBudgetLineItem,
  useGetBudgetByMovie,
  useGetBudgetTable,
  useCreateBudgetSubitem,
  useUpdateBudgetSubitem,
} from "@/services/queries/useBudget";
import { useGetProjects } from "@/services/queries/useProject";
import { BudgetLineItemFormData, BudgetPhase, BudgetTableStepResponse } from "@/types/orcamentos.interface";
import { BudgetSubitemFormData } from "@/components/orcamentos/schemas";
import { transformBudgetTableToPhases, findTopicById, findLineItemById } from "@/services/orcamentos/transform-budget-data";

export default function OrcamentosTabela() {
  const [, setLocation] = useLocation();

  // Fetch projects
  // Note: React Query + Axios api wrapper returns data directly (array of IProject)
  const { data: projectsData, isLoading: projectsLoading } = useGetProjects();
  const projects = useMemo(() => {
    if (!projectsData || !Array.isArray(projectsData)) return [];
    return projectsData;
  }, [projectsData]);

  // Selected project state
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);

  // Auto-select first project when loaded
  useEffect(() => {
    if (projects && projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  // Fetch budget summary first to get the budgetId for the current project
  // useGetBudgetByMovie uses movieId as parameter
  const {
    data: budgetData,
    isLoading: budgetLoading,
  } = useGetBudgetByMovie(selectedProjectId || 0, !!selectedProjectId);

  const budgetId = budgetData?.data?.id;

  // Then fetch the table specifically using budgetId
  const {
    data: tableDataResponse,
    isLoading: tableLoading,
    refetch: refetchTable,
  } = useGetBudgetTable(budgetId || 0, !!budgetId);

  const selectedBudgetId = budgetId;
  const isLoading = budgetLoading || tableLoading;



  // Transform budget data to hierarchical structure for display
  const budgetPhases: BudgetPhase[] = useMemo(() => {
    if (!tableDataResponse) return [];
    return transformBudgetTableToPhases(tableDataResponse as BudgetTableStepResponse[]);
  }, [tableDataResponse]);

  // Format projects for selector dropdown
  const availableProjects = projects?.map((p) => ({
    id: String(p.id),
    name: p.name,
    salic_number: p.salic_number,
  })) || [];

  const currentSelectedProject = availableProjects.find(
    (p) => p.id === String(selectedProjectId)
  ) || availableProjects[0];

  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    itemId: "",
    itemName: "",
    phaseId: "",
    topicId: "",
    isDeleting: false,
  });

  // Line item dialog state
  const [lineItemDialog, setLineItemDialog] = useState<{
    open: boolean;
    mode: "create" | "edit";
    phaseId: string;
    topicId: string;
    topicName: string;
    itemId?: string;
  }>({
    open: false,
    mode: "create",
    phaseId: "",
    topicId: "",
    topicName: "",
    itemId: undefined,
  });

  // Payment form dialog state
  const [paymentFormOpen, setPaymentFormOpen] = useState(false);

  // Subitem dialog state
  const [subitemDialog, setSubitemDialog] = useState<{
    open: boolean;
    mode: "create" | "edit";
    phaseId: string;
    topicId: string;
    topicName: string;
    itemId?: string;
  }>({
    open: false,
    mode: "create",
    phaseId: "",
    topicId: "",
    topicName: "",
    itemId: undefined,
  });

  // Mutations
  const createMutation = useCreateBudgetLineItem();
  const updateMutation = useUpdateBudgetLineItem();
  const createSubitemMutation = useCreateBudgetSubitem();
  const updateSubitemMutation = useUpdateBudgetSubitem();
  const uploadNFMutation = useUploadNFBudget();
  const deleteMutation = useDeleteBudgetLineItem();

  /**
   * Handle project change
   */
  const handleProjectChange = (projectId: string) => {
    try {
      setSelectedProjectId(parseInt(projectId));
      const project = availableProjects.find((p) => p.id === projectId);
      if (project) {
        toast({
          title: "Projeto selecionado",
          description: `Carregando dados de ${project.name}`,
        });
      }
    } catch (error) {
      toast({
        title: "Erro ao carregar projeto",
        description: "Não foi possível carregar os dados do projeto",
        variant: "destructive",
      });
      console.error("Error changing project:", error);
    }
  };

  /**
   * Navigate back to card view
   */
  const handleBackToCards = () => {
    try {
      setLocation("/financeiro/orcamentos");
    } catch (error) {
      toast({
        title: "Erro ao navegar",
        description: "Não foi possível voltar para a visualização em cards",
        variant: "destructive",
      });
      console.error("Navigation error:", error);
    }
  };

  /**
   * Handle novo lançamento (new launch/entry)
   * Opens dialog to create new budget line item in the first available topic
   */
  const handleNovoLancamento = () => {
    try {
      const firstPhase = budgetPhases[0];

      if (!firstPhase) {
        toast({
          title: "Nenhuma fase disponível",
          description: "Crie uma fase primeiro para adicionar itens",
          variant: "destructive",
        });
        return;
      }

      const firstTopic = firstPhase.topics[0];

      if (!firstTopic) {
        // If no topics, create with phase info
        setSubitemDialog({
          open: true,
          mode: "create",
          phaseId: firstPhase.id,
          topicId: `${firstPhase.id}-1`,
          topicName: "Novo Subitem",
          itemId: undefined,
        });
        return;
      }

      setSubitemDialog({
        open: true,
        mode: "create",
        phaseId: firstPhase.id,
        topicId: firstTopic.id,
        topicName: firstTopic.name,
        itemId: undefined,
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível abrir novo lançamento",
        variant: "destructive",
      });
      console.error("Novo lançamento error:", error);
    }
  };

  /**
   * Handle view item
   */
  const handleViewItem = (phaseId: string, topicId: string, itemId: string) => {
    try {
      const result = findTopicById(budgetPhases, itemId);

      if (!result) {
        throw new Error("Item não encontrado");
      }

      toast({
        title: "Visualizar Item",
        description: `Visualizando: ${result.topic.name}`,
      });

      // TODO: Implement view dialog
      console.log("View item:", { phaseId, topicId, itemId, item: result.topic });
    } catch (error) {
      toast({
        title: "Erro ao visualizar item",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
      console.error("Error viewing item:", error);
    }
  };

  /**
   * Handle edit item
   */
  const handleEditItem = (phaseId: string, topicId: string, itemId: string) => {
    try {
      const result = findTopicById(budgetPhases, itemId);

      if (!result) {
        throw new Error("Item não encontrado");
      }

      setSubitemDialog({
        open: true,
        mode: "edit",
        phaseId,
        topicId: "", // Since Subitem is now the topic, topicId is essentially redundant
        topicName: result.topic.name,
        itemId,
      });
    } catch (error) {
      toast({
        title: "Erro ao editar item",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
      console.error("Error editing item:", error);
    }
  };

  /**
   * Open delete confirmation dialog
   */
  const openDeleteDialog = (phaseId: string, topicId: string, itemId: string) => {
    try {
      const result = findTopicById(budgetPhases, itemId);

      if (!result) {
        throw new Error("Item não encontrado");
      }

      setDeleteDialog({
        open: true,
        itemId,
        itemName: result.topic.name,
        phaseId,
        topicId: "",
        isDeleting: false,
      });
    } catch (error) {
      toast({
        title: "Erro ao deletar item",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    }
  };

  /**
   * Close delete confirmation dialog
   */
  const closeDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      itemId: "",
      itemName: "",
      phaseId: "",
      topicId: "",
      isDeleting: false,
    });
  };

  /**
   * Confirm item deletion
   */
  const handleConfirmDelete = async () => {
    if (!selectedBudgetId) {
      toast({
        title: "Erro",
        description: "Nenhum orçamento selecionado",
        variant: "destructive",
      });
      return;
    }

    try {
      setDeleteDialog((prev) => ({ ...prev, isDeleting: true }));

      await deleteMutation.mutateAsync({
        budgetId: selectedBudgetId,
        itemId: parseInt(deleteDialog.itemId),
      });

      closeDeleteDialog();
      refetchTable();
    } catch (error) {
      toast({
        title: "Erro ao deletar item",
        description: error instanceof Error ? error.message : "Erro ao deletar item",
        variant: "destructive",
      });
      console.error("Error deleting item:", error);
    } finally {
      setDeleteDialog((prev) => ({ ...prev, isDeleting: false }));
    }
  };

  /**
   * Handle add topic to phase
   */
  const handleAddTopic = (phaseId: string) => {
    try {
      const phase = budgetPhases.find((p) => p.id === phaseId);

      if (!phase) {
        throw new Error("Fase não encontrada");
      }

      setSubitemDialog({
        open: true,
        mode: "create",
        phaseId,
        topicId: "",
        topicName: "Novo Subitem",
        itemId: undefined,
      });
    } catch (error) {
      toast({
        title: "Erro ao adicionar tópico",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
      console.error("Error adding topic:", error);
    }
  };

  /**
   * Handle add line item to topic — opens payment form modal
   */
  const handleAddLineItem = (_phaseId: string, _topicId: string) => {
    setPaymentFormOpen(true);
  };

  /**
   * Close line item dialog
   */
  const closeLineItemDialog = () => {
    setLineItemDialog({
      open: false,
      mode: "create",
      phaseId: "",
      topicId: "",
      topicName: "",
      itemId: undefined,
    });
  };

  /**
   * Close subitem dialog
   */
  const closeSubitemDialog = () => {
    setSubitemDialog({
      open: false,
      mode: "create",
      phaseId: "",
      topicId: "",
      topicName: "",
      itemId: undefined,
    });
  };

  /**
   * Save line item (create or update)
   */
  const handleLineItemSave = async (data: BudgetLineItemFormData) => {
    if (!selectedBudgetId) {
      toast({
        title: "Erro",
        description: "Nenhum orçamento encontrado para o projeto selecionado",
        variant: "destructive",
      });
      return;
    }

    try {
      let fiscalNoteUrl: string | null = null;

      // Upload fiscal note if provided
      if (data.attachments) {
        const formData = new FormData();
        formData.append("file", data.attachments);
        const uploadResult = await uploadNFMutation.mutateAsync({
          data: formData,
          budgetId: selectedBudgetId,
        });
        fiscalNoteUrl = uploadResult?.url || null;
      }

      const payload = {
        ...data,
        fiscal_note_url: fiscalNoteUrl,
      };

      if (lineItemDialog.mode === "create") {
        await createMutation.mutateAsync({
          budgetId: selectedBudgetId,
          phaseId: lineItemDialog.phaseId,
          data: payload,
        });
      } else if (lineItemDialog.itemId) {
        await updateMutation.mutateAsync({
          budgetId: selectedBudgetId,
          itemId: lineItemDialog.itemId,
          data: payload,
        });
      }

      closeLineItemDialog();
      refetchTable();
    } catch (error) {
      console.error("Error saving line item:", error);
    }
  };

  /**
   * Save subitem (create or update)
   */
  const handleSubitemSave = async (data: BudgetSubitemFormData) => {
    if (!selectedBudgetId) {
      toast({
        title: "Erro",
        description: "Nenhum orçamento encontrado para o projeto selecionado",
        variant: "destructive",
      });
      return;
    }

    try {
      if (subitemDialog.mode === "create") {
        await createSubitemMutation.mutateAsync({
          budgetId: selectedBudgetId,
          phaseId: subitemDialog.phaseId,
          data: data,
        });
      } else if (subitemDialog.itemId) {
        await updateSubitemMutation.mutateAsync({
          budgetId: selectedBudgetId,
          itemId: subitemDialog.itemId,
          data: data,
        });
      }

      closeSubitemDialog();
      refetchTable();
    } catch (error) {
      console.error("Error saving subitem:", error);
    }
  };

  /**
   * Get initial data for edit mode on Subitems
   */
  const getSubitemInitialData = () => {
    if (subitemDialog.mode !== "edit" || !subitemDialog.itemId) {
      return undefined;
    }

    const result = findTopicById(budgetPhases, subitemDialog.itemId);

    if (!result?.topic) return undefined;

    return {
      phaseId: result.phase.id,
      name: result.topic.name,
      value: result.topic.orcamentoAprovado, // mapping back
    } as BudgetSubitemFormData;
  };

  /**
   * Get initial data for edit mode on Payments
   */
  const getLineItemInitialData = () => {
    if (lineItemDialog.mode !== "edit" || !lineItemDialog.itemId) {
      return undefined;
    }

    const result = findLineItemById(budgetPhases, lineItemDialog.itemId);

    if (!result?.item) return undefined;

    return {
      id: result.item.id,
      name: result.item.name,
      paymentDate: result.item.paymentDate,
      paymentMethod: result.item.paymentMethod,
      value: result.item.value,
      status: result.item.status,
      lastApprovedBudget: result.item.lastApprovedBudget,
      fiscalDocType: result.item.fiscalDocType || null,
      fiscalDocNumber: result.item.fiscalDocNumber || null,
    } as BudgetLineItemFormData;
  };

  // Loading state
  if (projectsLoading) {
    return (
      <AppContainer title="Tabela Orçamentária" description="Carregando projetos...">
        <div className="flex items-center justify-center min-h-[400px]">
          <p className="text-gray-400">Carregando projetos...</p>
        </div>
      </AppContainer>
    );
  }

  // No projects state
  if (!projects || projects.length === 0) {
    return (
      <AppContainer title="Tabela Orçamentária" description="Nenhum projeto encontrado">
        <div className="flex items-center justify-center min-h-[400px]">
          <p className="text-gray-400">Nenhum projeto encontrado. Crie um projeto primeiro.</p>
        </div>
      </AppContainer>
    );
  }

  return (
    <AppContainer
      title="Tabela Orçamentária"
      description="Visualize e gerencie os itens do orçamento em formato de tabela"
      button={
        <Button
          onClick={handleBackToCards}
          variant="outline"
        >
          Ir para Orçamentos
        </Button>
      }
    >
      {/* Project Selector */}
      <div className="mt-2">
        <ProjectSelector
          projects={availableProjects}
          selectedProject={currentSelectedProject}
          onProjectChange={handleProjectChange}
        />
      </div>

      {/* Loading budget */}
      {isLoading && (
        <div className="flex items-center justify-center min-h-[200px]">
          <p className="text-gray-400">Carregando orçamento...</p>
        </div>
      )}

      {/* No budget found */}
      {!isLoading && !budgetId && selectedProjectId && (
        <div className="flex items-center justify-center min-h-[200px]">
          <p className="text-gray-400">
            Nenhum orçamento encontrado para este projeto. Crie um orçamento primeiro.
          </p>
        </div>
      )}

      {/* Budget Table */}
      {!isLoading && budgetId && !!tableDataResponse && (
        <BudgetTable
          data={budgetPhases}
          onViewItem={handleViewItem}
          onEditItem={handleEditItem}
          onDeleteItem={openDeleteDialog}
          onAddTopic={handleAddTopic}
          onAddLineItem={handleAddLineItem}
          onNovoLancamento={handleNovoLancamento}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeletePhaseDialog
        open={deleteDialog.open}
        phaseName={deleteDialog.itemName}
        isDeleting={deleteDialog.isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={closeDeleteDialog}
      />

      {/* Budget Line Item Dialog (Pagamentos) */}
      <BudgetLineItemDialog
        open={lineItemDialog.open}
        mode={lineItemDialog.mode}
        phaseId={lineItemDialog.phaseId}
        topicId={lineItemDialog.topicId}
        topicName={lineItemDialog.topicName}
        itemId={lineItemDialog.itemId}
        initialData={getLineItemInitialData()}
        onClose={closeLineItemDialog}
        onSave={handleLineItemSave}
        isSaving={createMutation.isPending || updateMutation.isPending || uploadNFMutation.isPending}
      />

      {/* Payment Form Modal (+ Pagamento) */}
      <PaymentFormDialog
        open={paymentFormOpen}
        onClose={() => setPaymentFormOpen(false)}
        projetoSelecionado={projects.find(p => p.id === selectedProjectId) ?? null}
        projects={availableProjects}
      />

      {/* Budget Subitem Dialog (Sub-itens) */}
      <BudgetSubitemDialog
        open={subitemDialog.open}
        mode={subitemDialog.mode}
        phaseId={subitemDialog.phaseId}
        topicId={subitemDialog.topicId}
        topicName={subitemDialog.topicName}
        itemId={subitemDialog.itemId}
        initialData={getSubitemInitialData()}
        phases={budgetPhases.map(p => ({ id: p.id, name: p.phaseName }))}
        onClose={closeSubitemDialog}
        onSave={handleSubitemSave}
        onDelete={() => {
          if (subitemDialog.itemId) {
            openDeleteDialog(subitemDialog.phaseId, "", subitemDialog.itemId);
          }
        }}
        isSaving={createSubitemMutation.isPending || updateSubitemMutation.isPending}
      />
    </AppContainer>
  );
}
